import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { HIW_FAQ } from '@/pages/how-it-works/data';

/**
 * Mechanics-focused FAQ for the How It Works page.
 *
 * Kept to questions that belong to *this* page (entry logic, stops, symbol,
 * frequency, news, settings) so it complements — rather than duplicates —
 * the general FAQ page. The same array drives the page's FAQPage schema.
 */
export default function FaqSection() {
  const [openId, setOpenId] = useState<string | null>(HIW_FAQ[0].id);

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <section id="faq" className="py-14 md:py-20 bg-background-50 border-y border-background-200 scroll-mt-32 md:scroll-mt-36">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="FAQ"
              title="The things people ask before they run it"
              subtitle="Short answers to the mechanical questions this page tends to raise. Anything about pricing, brokers or deposits lives on the FAQ page."
            />
          </Reveal>

          <div className="mt-10 md:mt-12 max-w-3xl mx-auto flex flex-col gap-3">
            {HIW_FAQ.map((item, index) => {
              const open = openId === item.id;
              return (
                <Reveal key={item.id} delay={index * 50}>
                  <div
                    className={`rounded-lg border transition-colors duration-200 ${
                      open
                        ? 'border-accent-300 bg-accent-50'
                        : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(item.id)}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 md:py-5 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 rounded-lg"
                    >
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-950">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                          open ? 'bg-accent-500 text-primary-950 rotate-180' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
                      </span>
                    </button>
                    {/* Auto-height reveal via grid rows — no fixed max-height, so
                        longer answers can never be clipped. */}
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-5 text-xs md:text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}